import { FC, useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import Cookies from 'js-cookie';

import './Charts.scss';

type LineChartProps = {
  name: string;
  data: any[];
  data2?: any[];
  xLabels?: any;
  titleX?: string;
  titleY?: string;
  selectedQuarter?: { from: string; to: string };
  chartHeight?: number;
};

const LineChart: FC<LineChartProps> = ({
  name,
  data,
  data2,
  xLabels,
  titleX,
  titleY,
  selectedQuarter,
  chartHeight = 450,
}) => {
  const chartRootRef = useRef<am5.Root | null>(null);

  useLayoutEffect(() => {
    am5.addLicense('AM5M326788873');
    am5.addLicense('AM5C326788873');

    // Cleanup existing chart root
    if (chartRootRef.current) {
      chartRootRef.current.dispose();
    }

    const root = am5.Root.new(`${name}-chart`);
    chartRootRef.current = root;

    root.setThemes([am5themes_Animated.new(root)]);

    let colorSet = am5.ColorSet.new(root, {});
    if (selectedQuarter && selectedQuarter.to) {
      const fromIndex = data.findIndex((e) => e.name === selectedQuarter.from);
      const toIndex = data.findIndex((e) => e.name === selectedQuarter.to);
      if (fromIndex >= 0) data[fromIndex].fillSettings = { fill: colorSet.getIndex(3) };
      if (toIndex >= 0) data[toIndex].fillSettings = { fill: colorSet.getIndex(0) };
    }

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xRenderer.labels.template.setAll({
      oversizedBehavior: 'wrap',
      maxWidth: 150,
      fontSize: Number(Cookies.get('globalFontSize')) * 0.79,
      opposite: true,
      fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
      ...xLabels,
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'name',
        renderer: xRenderer,
      })
    );

    if (titleX) {
      xAxis.children.push(
        am5.Label.new(root, {
          text: titleX,
          fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
          fontWeight: '600',
          fontStyle: 'italic',
          x: am5.p50,
          centerX: am5.p50,
        })
      );
    }

    xAxis.data.setAll(data);

    const yRenderer = am5xy.AxisRendererY.new(root, { minGridDistance: 20 });
    yRenderer.labels.template.setAll({
      oversizedBehavior: 'wrap',
      maxWidth: 150,
      fontSize: Number(Cookies.get('globalFontSize')) * 0.79,
      fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0.1,
        renderer: yRenderer,
      })
    );

    if (titleY) {
      yAxis.children.unshift(
        am5.Label.new(root, {
          rotation: -90,
          text: titleY,
          fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
          fontWeight: '600',
          fontStyle: 'italic',
          y: am5.p50,
          centerX: am5.p50,
        })
      );
    }

    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Series 1',
        xAxis,
        yAxis,
        valueYField: 'value',
        categoryXField: 'name',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'horizontal',
          labelText: '[bold]{name}[/]\n{valueY}',
        }),
      })
    );

    series.fills.template.setAll({
      visible: true,
      fillOpacity: 0.5,
      templateField: 'fillSettings',
    });

    if (data2) {
      const series2 = chart.series.push(
        am5xy.SmoothedXLineSeries.new(root, {
          name: 'Series 2',
          xAxis,
          yAxis,
          valueYField: 'value',
          categoryXField: 'name',
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: '[bold]{name}[/]\n{valueY}',
          }),
        })
      );
      series2.data.setAll(data2);
    }

    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomXY',
      })
    );

    series.data.setAll(data);
    series.appear(1000);
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [name, data, data2, xLabels, titleX, titleY, selectedQuarter]);

  return <div id={`${name}-chart`} className="chart-container" style={{ height: chartHeight }} />;
};

export default LineChart;
